import { ArrowUpRight, Sparkles, RefreshCw, Library } from "lucide-react";
import { Link } from "react-router-dom";

const PROMPTS = [
  "01  Write a concise client email",
  "02  Turn notes into a clean summary",
  "03  Draft a 5-line product caption",
  "04  Brainstorm 10 article ideas",
  "05  Outline a daily writing routine",
];

const Hero = () => {
  const scrollTo = (id) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="top"
      data-testid="section-hero"
      className="relative bg-[#F7F7F4]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-20 md:pb-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left — copy */}
          <div className="lg:col-span-7">
            <div className="overline fade-up">— Digital product studio</div>

            <h1
              data-testid="hero-headline"
              className="mt-6 font-sora text-[42px] leading-[1.02] sm:text-[56px] lg:text-[76px] font-extrabold tracking-tight text-[#111111] fade-up fade-up-delay-1"
            >
              Simple digital tools{" "}
              <br className="hidden sm:block" />
              for{" "}
              <span className="relative inline-block">
                <span className="relative z-10">smarter work</span>
                <span
                  aria-hidden
                  className="absolute left-0 right-0 bottom-1 h-[10px] bg-[#2563EB]/15 z-0"
                />
              </span>
              .
            </h1>

            <p
              data-testid="hero-subhead"
              className="mt-7 max-w-xl font-manrope text-[17px] md:text-[19px] leading-[1.65] text-[#111111]/70 fade-up fade-up-delay-2"
            >
              Vaymilo creates practical AI kits, templates, and workflows that
              help you write, plan, organize, and create faster — without
              overcomplicating your day.
            </p>

            <div
              data-testid="hero-trust-pills"
              className="mt-6 flex flex-wrap items-center gap-2.5 fade-up fade-up-delay-2"
            >
              {[
                { icon: Library, label: "All-access library" },
                { icon: RefreshCw, label: "Cancel anytime" },
                { icon: Sparkles, label: "New drops monthly" },
              ].map(({ icon: Icon, label }) => (
                <span
                  key={label}
                  className="inline-flex items-center gap-1.5 bg-white border border-[#E7E7E2] rounded-full px-3.5 py-1.5 font-manrope text-[13px] font-medium text-[#111111]/75"
                >
                  <Icon size={13} strokeWidth={2.25} className="text-[#2563EB]" />
                  {label}
                </span>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-3 fade-up fade-up-delay-3">
              <Link
                to="/products"
                data-testid="btn-hero-primary"
                className="inline-flex items-center gap-2 bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-manrope font-medium text-[15px] px-6 py-3.5 rounded-lg transition-colors"
              >
                Explore products
                <ArrowUpRight size={16} strokeWidth={2.25} />
              </Link>
              <button
                onClick={() => scrollTo("#free-tools")}
                data-testid="btn-hero-secondary"
                className="inline-flex items-center gap-2 bg-white text-[#111111] font-manrope font-medium text-[15px] px-6 py-3.5 rounded-lg border border-[#E7E7E2] hover:border-[#111111] transition-colors"
              >
                Get free tools
              </button>
            </div>

            <p
              data-testid="hero-trustline"
              className="mt-6 font-manrope text-sm text-[#111111]/55 fade-up fade-up-delay-4"
            >
              Built for students, creators, freelancers, and modern teams.
            </p>
          </div>

          {/* Right — text-based preview card (no images, no fake UI) */}
          <div className="lg:col-span-5">
            <div
              data-testid="hero-preview-card"
              className="relative bg-white border border-[#E7E7E2] rounded-2xl p-7 md:p-8 fade-up fade-up-delay-2"
            >
              {/* Card meta strip */}
              <div className="flex items-center justify-between text-[11px] font-mono uppercase tracking-[0.18em] text-[#111111]/45">
                <span>Kit · 001</span>
                <span className="text-[#2563EB]">New</span>
              </div>

              <h3 className="mt-5 font-sora text-[26px] md:text-[28px] font-bold text-[#111111] leading-tight tracking-tight">
                Vaymilo AI Writing Kit
              </h3>
              <p className="mt-3 font-manrope text-[15px] text-[#111111]/65 leading-relaxed">
                Prompts, workflows, and ready-to-use templates for everyday
                writing.
              </p>

              <div className="mt-6 h-px bg-[#E7E7E2]" />

              <div className="mt-6">
                <div className="text-[11px] font-sora font-bold tracking-[0.2em] uppercase text-[#111111]/50">
                  Inside the kit
                </div>
                <ul className="mt-4 space-y-3">
                  {[
                    "Email prompts",
                    "Caption prompts",
                    "Summary prompts",
                    "Idea prompts",
                    "Daily writing workflows",
                  ].map((item) => (
                    <li
                      key={item}
                      className="flex items-baseline gap-3 font-manrope text-[15px] text-[#111111]"
                    >
                      <span className="text-[#2563EB] font-mono text-xs">
                        +
                      </span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-7 h-px bg-[#E7E7E2]" />

              {/* Compact prompt list */}
              <div className="mt-6">
                <div className="text-[11px] font-sora font-bold tracking-[0.2em] uppercase text-[#111111]/50">
                  Sample prompts
                </div>
                <ol className="mt-3 space-y-1.5 font-mono text-[12.5px] text-[#111111]/70 leading-relaxed">
                  {PROMPTS.map((p) => (
                    <li key={p} className="truncate">
                      {p}
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
